<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from "vue";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getPublicModeHint,
} from "@/utils/permissionDefaults";
import {
  ensureLocalDateTime,
  formatDateTimeForInput,
  localDateTimeToUtc,
} from "@/utils/dateUtils";
import {
  PROGRESS_OPTIONS,
  progressShortLabel,
  progressFullLabel,
  progressIcon,
  isSameProgress,
  PLANNING_PROGRESS,
} from "@/utils/activityProgress";
import { plaintextToEditorHtml } from "@/utils/tiptapEditorConfig";
import RichTextEditor from "../Generics/RichTextEditor.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  activityId: { type: Number, required: true },
  textId: { type: Number, required: true },
  startInPlanningMode: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "refresh"]);

const activityStore = useActivityStore();
const userStore = useUserStore();
const groupStore = useGroupStore();
const permissionStore = usePermissionStore();

const obj = ref({ permissions: [] });
const isPlanned = ref(false);
const effectiveDatesBackup = ref({ startedAt: "", endedAt: "", status: null });

function handlePlanningToggle() {
  if (isPlanned.value) {
    effectiveDatesBackup.value = {
      startedAt: obj.value.startedAt || "",
      endedAt: obj.value.endedAt || "",
      status: obj.value.status,
    };
    obj.value.startedAt = "";
    obj.value.endedAt = "";
    const defaultPlannedDate = formatDateTimeForInput(new Date());
    obj.value.plannedStartAt = defaultPlannedDate;
    obj.value.plannedEndAt = defaultPlannedDate;
    obj.value.status = "Em andamento";
  } else {
    obj.value.plannedStartAt = "";
    obj.value.plannedEndAt = "";
    obj.value.startedAt = effectiveDatesBackup.value.startedAt;
    obj.value.endedAt = effectiveDatesBackup.value.endedAt;
    obj.value.status = effectiveDatesBackup.value.status || obj.value.status;
  }
}
const isSubmitting = ref(false);
const isLoading = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// 🔹 NOVO: expandir/colapsar a descrição (espelho do AddNoteForm)
const isBodyExpanded = ref(false);
function handleToggleBodyExpand() {
  if (isLoading.value || isSubmitting.value) return;
  isBodyExpanded.value = !isBodyExpanded.value;
}

const creatorUserName = ref("");

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Ao salvar com sucesso, esconde só o painel do modal imediatamente (o componente
// segue montado até o emit("close"), então o toast — que fica num Teleport próprio —
// continua visível sozinho, sem o modal sobreposto atrás.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
  if (typeof onFinish === "function") {
    // Esconde o painel do modal já — o toast segue visível sozinho até o
    // onFinish (emit close), que desmonta o componente após o tempo do toast.
    hideBackdropForSuccess.value = true;
    setTimeout(onFinish, 2000);
  }
}

// ====================================================================
// COMPUTED LISTS
// ====================================================================
const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({
    id: u.userId,
    name: u.userName,
    photoUrl:
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      u.userPhotoPath ??
      u.user_photo_path ??
      null,
    photoPath: u.photoPath ?? u.photo_path ?? u.photoUrl ?? u.photo_url ?? null,
    userPhotoPath:
      u.userPhotoPath ??
      u.user_photo_path ??
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      null,
  }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

const closeModal = () => emit("close");

// ====================================================================
// HELPERS DE PERMISSÃO
// ====================================================================
const loggedUserId = computed(() => userStore.currentUser?.userId ?? null);

function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find(
      (g) => g.groupName?.toString().trim().toLowerCase() === "todos"
    )?.groupId ?? null
  );
}

function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

function getCreatorId() {
  if (!creatorUserName.value) return loggedUserId.value;
  const found = (userStore.users || []).find(
    (u) => u.userName === creatorUserName.value
  );
  return found?.userId ?? loggedUserId.value;
}

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = getCreatorId();
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

function buildPrivatePermissions() {
  const creatorId = getCreatorId();
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];

  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}

// ====================================================================
// currentAccessMode — computed de 3 estados
// ====================================================================
const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];
  if (perms.length === 0) return "public";

  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";

  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms))
    return "private";

  return "custom";
});

// ====================================================================
// selectAccessMode
// ====================================================================
function selectAccessMode(mode) {
  if (isLoading.value || isSubmitting.value) return;

  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

// ====================================================================
// WATCH: Data de Conclusão → status "Concluída"
// ====================================================================
watch(
  () => obj.value.endedAt,
  (newVal) => {
    if (newVal && obj.value.status !== "Concluída") {
      obj.value.status = "Concluída";
    }
  }
);

// ====================================================================
// PROGRESSO: cor de fundo por opção (apenas background do dropdown)
// ====================================================================
function progressColor(status) {
  switch (status) {
    case "Em andamento":
      return "#fff3bf";
    case "Concluída para correção":
      return "#ffd8a8";
    case "Em correção":
      return "#ffc9c9";
    case "Corrigida":
      return "#b2f2bb";
    case "Concluída":
      return "#a5d8ff";
    default:
      return "";
  }
}

// Cor do texto correspondente a cada progresso (mesma paleta do ActivityForm)
function progressTextColor(status) {
  switch (status) {
    case "Em andamento":
      return "#f59f00";
    case "Concluída para correção":
      return "#e8590c";
    case "Em correção":
      return "#c92a2a";
    case "Corrigida":
      return "#2b8a3e";
    case "Concluída":
      return "#1971c2";
    default:
      return "";
  }
}

const formPlanningProgress = computed(() => {
  if (!isPlanned.value) return null;
  if (!obj.value.plannedStartAt) return PLANNING_PROGRESS.future;

  const plannedDay = obj.value.plannedStartAt.slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type) => parts.find((item) => item.type === type)?.value;
  const today = `${part("year")}-${part("month")}-${part("day")}`;

  if (plannedDay === today) return PLANNING_PROGRESS.today;
  return plannedDay > today
    ? PLANNING_PROGRESS.future
    : PLANNING_PROGRESS.overdue;
});

const displayedProgressLabel = computed(
  () => formPlanningProgress.value?.short || progressShortLabel(obj.value.status)
);
const displayedProgressTitle = computed(
  () => formPlanningProgress.value?.label || progressFullLabel(obj.value.status)
);
const displayedProgressBackground = computed(() => {
  switch (formPlanningProgress.value?.colorClass) {
    case "status-future":
      return "#ede9fe";
    case "status-due-today":
      return "#dbeafe";
    case "status-overdue":
      return "#fee2e2";
    default:
      return progressColor(obj.value.status);
  }
});
const displayedProgressTextColor = computed(() => {
  switch (formPlanningProgress.value?.colorClass) {
    case "status-future":
      return "#6d28d9";
    case "status-due-today":
      return "#1d4ed8";
    case "status-overdue":
      return "#b91c1c";
    default:
      return progressTextColor(obj.value.status);
  }
});
const displayedProgressIcon = computed(
  () => formPlanningProgress.value?.icon || progressIcon(obj.value.status)
);
const displayedProgressBorderColor = computed(() =>
  formPlanningProgress.value?.colorClass === "status-future"
    ? "#6d28d9"
    : "#cbd5e1"
);

// Dropdown customizado de progresso (mesmo estilo do ActivityForm)
const isProgressOpen = ref(false);
const progressRef = ref(null);

// PROGRESS_OPTIONS e rótulos vêm de @/utils/activityProgress — o mesmo estado
// aparece com o mesmo nome aqui, na tabela e no card.

function toggleProgressDropdown() {
  if (isLoading.value) return;
  isProgressOpen.value = !isProgressOpen.value;
}

// Espelha a regra da tabela: com o planejamento ligado, o status guardado em
// obj.status ("Em andamento") não é o estado corrente — o gatilho já exibe
// Futura/Iniciar hoje/Atrasada —, então nenhuma opção do fluxo fica marcada.
function isCurrentProgressOption(value) {
  return !isPlanned.value && isSameProgress(obj.value.status, value);
}

function selectProgress(value) {
  if (isPlanned.value) {
    // Selecionar um progresso operacional significa iniciar efetivamente a
    // atividade. As previsões permanecem no payload como histórico.
    isPlanned.value = false;
    obj.value.startedAt = formatDateTimeForInput(new Date());
  }

  obj.value.status = value;

  // "Concluída" → preenche a Data de Fim com a data/hora local atual (se vazia);
  // qualquer outra opção → esvazia a Data de Fim.
  if (value === "Concluída") {
    if (!obj.value.endedAt) {
      obj.value.endedAt = formatDateTimeForInput(new Date());
    }
  } else {
    obj.value.endedAt = "";
  }

  isProgressOpen.value = false;
}

function handleProgressClickOutside(event) {
  if (
    isProgressOpen.value &&
    progressRef.value &&
    !progressRef.value.contains(event.target)
  ) {
    isProgressOpen.value = false;
  }
}

// ====================================================================
// COMPUTED: habilitar/desabilitar botão Salvar
// ====================================================================
const isSaveDisabled = computed(() => {
  if (isLoading.value) return true;
  if (isSubmitting.value) return true;
  if (!(obj.value.title ?? "").trim()) return true;
  if (obj.value.status === "Concluída") {
    const end = obj.value.endedAt;
    if (!end || end === "") return true;
  }
  return false;
});

// ====================================================================
// MINI-MODAL DE PERMISSÕES
// ====================================================================
const showPermissionModal = ref(false);
const permissionBackup = ref([]);
const permissionFormKey = ref(0);
const permissionFormRef = ref(null);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));
  showPermissionModal.value = true;
}

function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    !permissionFormRef.value.validateBeforeConfirm()
  ) {
    return;
  }
  showPermissionModal.value = false;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

// ====================================================================
// CARREGAR DADOS
// ====================================================================
async function carregarDados() {
  try {
    const activity = await activityStore.getActivityById(props.activityId);

    if (activity) {
      const activityPermissions = await activityStore.fetchActivityPermissions(
        props.activityId
      );

      const formattedPermissions = (activityPermissions || []).map((p) => ({
        entityId: p.userId != null ? p.userId : p.groupId,
        permissionId: p.permissionId,
        type: p.userId != null ? "user" : "group",
      }));

      creatorUserName.value = activity.createdByUserName;

      const allPermissions = permissionStore.permissions || [];
      const creator =
        (userStore.users || []).find(
          (u) => u.userName === activity.createdByUserName
        ) || null;

      let mergedPermissions = [...formattedPermissions];

      if (creator && allPermissions.length > 0) {
        allPermissions.forEach((perm) => {
          const exists = mergedPermissions.some(
            (p) =>
              p.type === "user" &&
              p.entityId === creator.userId &&
              p.permissionId === perm.permissionId
          );
          if (!exists) {
            mergedPermissions.push({
              entityId: creator.userId,
              type: "user",
              permissionId: perm.permissionId,
            });
          }
        });
      }

      obj.value = {
        activityId: activity.activityId,
        title: activity.title,
        // Descrição antiga (content_format 'plaintext') é convertida em
        // parágrafos ao entrar no editor — sem isso o RichTextEditor trataria
        // o texto puro como HTML literal, colapsando as quebras de linha.
        // O upgrade de formato só se efetiva ao salvar (ver salvarAlteracoes).
        description:
          activity.contentFormat === "html"
            ? activity.description ?? ""
            : plaintextToEditorHtml(activity.description ?? ""),
        status: activity?.status,
        active: activity.active,
        startedAt: formatDateTimeForInput(activity?.startedAt),
        endedAt: formatDateTimeForInput(activity?.endedAt),
        plannedStartAt: formatDateTimeForInput(activity?.plannedStartAt),
        plannedEndAt: formatDateTimeForInput(activity?.plannedEndAt),
        textId: activity.textId,
        permissions: mergedPermissions,
      };
      const alreadyPlanned = Boolean(
        activity?.plannedStartAt && !activity?.startedAt
      );
      isPlanned.value = alreadyPlanned;

      if (props.startInPlanningMode && !alreadyPlanned) {
        isPlanned.value = true;
        handlePlanningToggle();
      }

    } else {
      throw new Error("Atividade para edição não encontrada.");
    }
  } catch (error) {
    console.error("Erro ao carregar dados da atividade:", error);
    showErrorToast("Não foi possível carregar os dados da atividade.");
    closeModal();
  }
}

// ====================================================================
// SALVAR
// ====================================================================
async function salvarAlteracoes() {
  if (isSubmitting.value) return;

  if (!obj.value.title || obj.value.title.trim() === "") {
    showErrorToast("O campo 'Título' é obrigatório.");
    return;
  }

  if (
    obj.value.status === "Concluída" &&
    (!obj.value.endedAt || obj.value.endedAt === "")
  ) {
    showErrorToast(
      'Para marcar a atividade como "Concluída", informe a Data de Conclusão.'
    );
    return;
  }

  if (isPlanned.value) {
    if (!obj.value.plannedStartAt) {
      showErrorToast("Informe a data de início prevista.");
      return;
    }
    const plannedStart = new Date(obj.value.plannedStartAt);
    const plannedEnd = obj.value.plannedEndAt ? new Date(obj.value.plannedEndAt) : null;
    if (plannedEnd && plannedEnd < plannedStart) {
      showErrorToast("O fim previsto não pode ser anterior ao início previsto.");
      return;
    }
  }

  const payload = {
    ...obj.value,
    startedAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.startedAt)),
    endedAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.endedAt)),
    plannedStartAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.plannedStartAt)),
    plannedEndAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.plannedEndAt)),
  };

  isSubmitting.value = true;

  try {
    await activityStore.updateActivity(payload);
    await activityStore.fetchActivitiesPaged(
      activityStore.currentPage,
      activityStore.pageSize,
      activityStore.searchTerm,
      props.textId
    );

    showSuccessToast("Alterações salvas com sucesso.", () => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    });
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";

    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors;
      const messages = [];
      for (const field in validationErrors) {
        if (validationErrors[field]?.length)
          messages.push(validationErrors[field][0]);
      }
      if (messages.length) userFriendlyMessage = messages.join("\n");
    } else if (error.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    console.error(
      "Erro detalhado da API ao salvar:",
      error.response?.data || error
    );
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// ====================================================================
// KEYBOARD
// ====================================================================
const handleEscKey = (event) => {
  if (event.key === "Escape" && !isSubmitting.value) {
    emit("close");
  }
};

const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;
  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;
  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;
  if (!isSubmitting.value) {
    event.preventDefault();
    salvarAlteracoes();
  }
};

// ====================================================================
// MOUNT / UNMOUNT
// ====================================================================
onMounted(async () => {
  isLoading.value = true;

  await Promise.all([
    userStore.fetchUsers(1, 1000, ""),
    groupStore.fetchGroups(1, 1000, ""),
    permissionStore.fetchAllPermissions(),
  ]);

  await carregarDados();

  isLoading.value = false;

  if (props.startInPlanningMode) {
    await nextTick();
    window.requestAnimationFrame(() => {
      document.getElementById("plannedStartDate")?.focus();
    });
  }

  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("keydown", handleEnterKey);
  document.addEventListener("click", handleProgressClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("keydown", handleEnterKey);
  document.removeEventListener("click", handleProgressClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div v-show="!hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Atividade</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="salvarAlteracoes" novalidate class="modal-form">
        <div class="modal-body" :class="{ 'modal-body--expanded': isBodyExpanded }">
          <transition name="loading-fade">
            <div
              v-if="isLoading"
              class="loading-overlay"
              aria-label="Carregando..."
            >
              <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <span class="loading-text">Carregando...</span>
            </div>
          </transition>

          <div
            class="step-content"
            :class="{ 'step-content--expanded': isBodyExpanded }"
          >
            <!-- 🔹 ALTERADO: oculta o toggle quando a descrição está expandida -->
            <div class="form-group-toggle" v-show="!isBodyExpanded">
              <label for="active" class="form-label">Atividade Ativa</label>
              <label class="toggle-switch">
                <input
                  v-model="obj.active"
                  type="checkbox"
                  id="active"
                  :disabled="isLoading"
                />
                <span class="slider"></span>
              </label>
            </div>

            <div
              class="form-group"
              style="margin-top: -2.5rem"
              v-show="!isBodyExpanded"
            >
              <label for="title" class="form-label">Título</label>
              <input
                v-model="obj.title"
                type="text"
                id="title"
                class="input-base"
                placeholder="Digite o título da atividade"
                required
                :disabled="isLoading"
                @focus="$event.target.select()"
              />
            </div>

            <div
              class="form-group"
              :class="{ 'form-group--desc-expanded': isBodyExpanded }"
            >
              <!-- 🔹 ALTERADO: label agora compartilha a linha com o botão de expandir/colapsar -->
              <div class="body-label-row">
                <label for="description" class="form-label"
                  >Descrição (opcional)</label
                >

                <!-- 🔹 NOVO: botão expandir/colapsar (espelho do AddNoteForm) -->
                <button
                  type="button"
                  class="ct-expand-btn-inline"
                  :disabled="isLoading || isSubmitting"
                  @click="handleToggleBodyExpand"
                  :aria-label="
                    isBodyExpanded
                      ? 'Colapsar descrição'
                      : 'Expandir descrição'
                  "
                  :title="
                    isBodyExpanded
                      ? 'Colapsar descrição'
                      : 'Expandir descrição'
                  "
                >
                  <i
                    v-if="!isBodyExpanded"
                    class="fa fa-expand"
                    aria-hidden="true"
                  ></i>
                  <i v-else class="fa fa-compress" aria-hidden="true"></i>
                </button>
              </div>
              <RichTextEditor
                v-model="obj.description"
                placeholder="Descreva sua atividade"
                :max-length="5000"
                :unwrap-pasted-line-breaks="true"
                :start-collapsed="true"
                :disabled="isLoading"
              />
            </div>

            <div class="edit-timing-grid" v-show="!isBodyExpanded">
              <div class="form-group">
                <label for="status" class="form-label">Progresso</label>
                <div class="progress-select" ref="progressRef">
                  <button
                    type="button"
                    id="status"
                    class="progress-trigger"
                    :style="{
                      backgroundColor: displayedProgressBackground,
                      color: displayedProgressTextColor,
                      borderColor: displayedProgressBorderColor,
                    }"
                    :disabled="isLoading"
                    @click="toggleProgressDropdown"
                  >
                    <i
                      :class="['fas', displayedProgressIcon, 'progress-trigger-icon']"
                      aria-hidden="true"
                    ></i>
                    <span
                      class="progress-trigger-label"
                      :title="displayedProgressTitle"
                      >{{ displayedProgressLabel }}</span
                    >
                    <i
                      class="fas fa-chevron-down progress-chevron"
                      :class="{ open: isProgressOpen }"
                    ></i>
                  </button>

                  <div v-if="isProgressOpen" class="progress-dropdown-menu">
                    <ul>
                      <li
                        v-for="opt in PROGRESS_OPTIONS"
                        :key="opt.value"
                        class="progress-option"
                        :class="{
 'progress-option--current': isCurrentProgressOption(
                            opt.value
                          ),
                        }"
                        :style="{
                          '--opt-color': progressColor(opt.value),
                          '--opt-text': progressTextColor(opt.value),
                        }"
                        :title="opt.label"
                        @click="selectProgress(opt.value)"
                      >
                        <span class="progress-option-label">
                          <i
                            :class="['fas', opt.icon, 'progress-option-icon']"
                            :style="{ color: progressTextColor(opt.value) }"
                          ></i>
                          {{ opt.short }}
                        </span>
                        <i
                          v-if="isCurrentProgressOption(opt.value)"
                          class="fas fa-check progress-check-icon"
                        ></i>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="form-group planning-control">
                <label for="futurePlanning" class="form-label">Planejar data futura</label>
                <label class="toggle-switch planning-toggle-switch">
                  <input
                    id="futurePlanning"
                    v-model="isPlanned"
                    type="checkbox"
                    :disabled="isLoading"
                    @change="handlePlanningToggle"
                  />
                  <span class="slider"></span>
                </label>
              </div>

              <div v-if="!isPlanned" class="form-group timing-date-field">
                <label for="startDate" class="form-label">Início efetivo</label>
                <input
                  v-model="obj.startedAt"
                  type="datetime-local"
                  id="startDate"
                  class="input-base"
                  :disabled="isLoading"
                  @change="obj.startedAt = ensureLocalDateTime(obj.startedAt)"
                />
              </div>
              <div v-else class="form-group timing-date-field">
                <label for="plannedStartDate" class="form-label">Início previsto</label>
                <input
                  v-model="obj.plannedStartAt"
                  type="datetime-local"
                  id="plannedStartDate"
                  class="input-base"
                  :disabled="isLoading"
                  required
                  @change="obj.plannedStartAt = ensureLocalDateTime(obj.plannedStartAt)"
                />
              </div>

              <div v-if="!isPlanned" class="form-group timing-date-field">
                <label for="endDate" class="form-label">Data de Conclusão</label>
                <input
                  v-model="obj.endedAt"
                  type="datetime-local"
                  id="endDate"
                  class="input-base"
                  :disabled="isLoading"
                  @change="obj.endedAt = ensureLocalDateTime(obj.endedAt)"
                />
              </div>
              <div v-else class="form-group timing-date-field">
                <label for="plannedEndDate" class="form-label">Fim previsto</label>
                <input
                  v-model="obj.plannedEndAt"
                  type="datetime-local"
                  id="plannedEndDate"
                  class="input-base"
                  :min="obj.plannedStartAt || undefined"
                  :disabled="isLoading"
                  title="Opcional"
                  @change="obj.plannedEndAt = ensureLocalDateTime(obj.plannedEndAt)"
                />
              </div>
            </div>

            <!-- ✅ Segmented control de acesso -->
            <!-- 🔹 ALTERADO: oculta o controle de acesso quando a descrição está expandida -->
            <div class="form-group" v-show="!isBodyExpanded">
              <label class="form-label">Quem pode ver esta atividade?</label>

              <div class="access-segment">
                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'public',
                    'segment-btn--public': currentAccessMode === 'public',
                  }"
                  :disabled="isLoading || isSubmitting"
                  @click="selectAccessMode('public')"
                >
                  <i class="fas fa-globe-americas segment-btn__icon"></i>
                  <span class="segment-btn__label">Público</span>
                  <!-- <span class="segment-btn__desc">Todos os usuários</span> -->
                </button>

                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'private',
                    'segment-btn--private': currentAccessMode === 'private',
                  }"
                  :disabled="isLoading || isSubmitting"
                  @click="selectAccessMode('private')"
                >
                  <i class="fas fa-lock segment-btn__icon"></i>
                  <span class="segment-btn__label">Privado</span>
                  <!-- <span class="segment-btn__desc">Só você</span> -->
                </button>

                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'custom',
                    'segment-btn--custom': currentAccessMode === 'custom',
                  }"
                  :disabled="isLoading || isSubmitting"
                  @click="selectAccessMode('custom')"
                >
                  <i class="fas fa-user-shield segment-btn__icon"></i>
                  <span class="segment-btn__label">Personalizado</span>
                  <!-- <span class="segment-btn__desc">Escolher usuários e grupos</span> -->
                </button>
              </div>

              <p class="access-hint">
                <template v-if="currentAccessMode === 'public'">
                  <i class="fas fa-info-circle"></i>
                  {{ getPublicModeHint() }} poderão visualizar e
                  selecionar esta atividade.
                </template>
                <template v-else-if="currentAccessMode === 'private'">
                  <i class="fas fa-info-circle"></i>
                  Somente você poderá acessar esta atividade.
                </template>
                <template v-else>
                  <i class="fas fa-info-circle"></i>
                  Acesso restrito às pessoas e grupos que você configurou.
                  <button
                    type="button"
                    class="access-hint__link"
                    @click="openPermissionModal"
                    :disabled="isLoading || isSubmitting"
                  >
                    Editar permissões
                  </button>
                </template>
              </p>
            </div>

            <div
              style="flex-grow: 1; margin-top: -1.3rem"
              v-show="!isBodyExpanded"
            ></div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSaveDisabled || isBodyExpanded"
          >
            <svg
              v-if="isSubmitting"
              class="mr-1 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              />
            </svg>
            <span>{{
              isSubmitting ? "Salvando..." : "Salvar Alterações"
            }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Mini-modal de permissões -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para esta
              atividade.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              :entityId="props.activityId"
              entityType="Activity"
              :creatorUserName="creatorUserName"
            />
          </div>

          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="confirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  height: 720px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-footer {
  bottom: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;

  /* 🔹 ALTERADO: reduz pela metade o respiro vertical antes do campo "Atividade Ativa" */
  margin-top: -0.75rem;
}

/* 🔹 NOVO: modo expandido — a descrição ocupa todo o painel */
.modal-body--expanded {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.step-content--expanded {
  flex: 1 1 auto;
  min-height: 0;
  /* sem o pull-up quando expandido, para não cortar o topo do editor */
  margin-top: 0;
}
.form-group--desc-expanded {
  flex: 1 1 auto;
  min-height: 0;
}
.form-group--desc-expanded :deep(.rte) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

/* A caixa do editor cresce, mas a área editável continuava presa ao teto de
   altura que o RichTextEditor aplica quando :start-collapsed — e o rodapé (o
   contador) parava junto com ela, no meio do painel, com o fundo sobrando
   embaixo. Esse teto vem de um style inline do próprio editor: só !important
   o desfaz. */
.form-group--desc-expanded :deep(.rte-content) {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none !important;
}

/* 🔹 NOVO: linha do label + botão de expandir (espelho do AddNoteForm) */
.body-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* 🔹 NOVO: botão expandir/colapsar (mesmo visual do AddNoteForm) */
.ct-expand-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: none;
  background: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease-in-out;
  flex-shrink: 0;
}
.ct-expand-btn-inline:hover:not(:disabled) {
  background: var(--neutral-400);
  transform: translateY(-1px);
}
.ct-expand-btn-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.edit-timing-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); align-items: end; gap: 0.75rem; }
.edit-timing-grid > * { min-width: 0; }
.planning-control { min-height: 57px; }
.planning-toggle-switch { margin-top: 6px; }
.timing-date-field .input-base { width: 100%; min-width: 0; padding-left: 0.45rem; padding-right: 0.3rem; font-size: 0.76rem; }
@media (max-width: 767px) {
  .edit-timing-grid { grid-template-columns: 1fr; align-items: stretch; }
  .timing-date-field .input-base { font-size: 0.85rem; }
}

.form-group-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

select.input-base {
  width: 100%;
}

/* =========================
   SEGMENTED CONTROL DE ACESSO
========================= */
.access-segment {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--text-muted);
}

.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn__icon {
  font-size: 1.1rem;
  line-height: 1;
}
.segment-btn__label {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
}
.segment-btn__desc {
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1;
  opacity: 0.8;
}

.segment-btn--public.segment-btn--active {
  border-color: var(--success);
  background-color: var(--success-bg);
  color: var(--success-text);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.12);
}

.segment-btn--private.segment-btn--active {
  border-color: var(--warning);
  background-color: var(--warning-bg);
  color: var(--warning-text);
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.12);
}

.segment-btn--custom.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

.access-hint {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.access-hint i {
  font-size: 0.75rem;
  flex-shrink: 0;
}

.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.access-hint__link:hover:not(:disabled) {
  color: #7c63f5;
}
.access-hint__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* =========================
   TOGGLE
========================= */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-200);
  transition: 0.25s;
  border-radius: 999px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  top: 3px;
  background-color: var(--surface);
  transition: 0.25s;
  border-radius: 50%;
}
.toggle-switch input:checked + .slider {
  background-color: var(--brand-light);
}
.toggle-switch input:checked + .slider:before {
  transform: translateX(16px);
}

/* =========================
   MINI-MODAL DE PERMISSÕES
========================= */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}

.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}

.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}

.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}
:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}
:deep(.table-wrap::-webkit-scrollbar) {
  width: 6px;
}
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}

.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* =========================
   LOADING OVERLAY
========================= */
.select-progress {
  height: 34px;
  line-height: 34px;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Progresso: dropdown customizado (mesmo estilo do ActivityForm) ──────── */
.progress-select {
  position: relative;
  width: 100%;
}
.progress-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.3rem;
  width: 100%;
  height: 34px;
  padding: 0 0.7rem;
  border: 1px solid var(--neutral-400);
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-dark);
  cursor: pointer;
  transition: filter 0.15s, transform 0.05s ease;
}
.progress-trigger-label {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.progress-trigger:hover:not(:disabled) {
  filter: brightness(0.95);
}
.progress-trigger:active:not(:disabled) {
  transform: translateY(1px);
}
.progress-trigger:focus,
.progress-trigger:focus-visible {
  outline: none;
  box-shadow: none;
}
.progress-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.progress-chevron {
  font-size: 0.7rem;
  opacity: 0.7;
  flex-shrink: 0;
  transition: transform 0.2s ease-in-out;
}
.progress-chevron.open {
  transform: rotate(180deg);
}

/* O menu tem a mesma largura do trigger (opção selecionada) */
.progress-dropdown-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 100;
  width: 100%;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}
.progress-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}
.progress-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.32rem 0.6rem;
  border-radius: 7px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.progress-option:hover:not(.progress-option--current) {
  background-color: var(--opt-color);
  color: var(--opt-text);
}
.progress-option:active {
  transform: translateY(1px);
}
.progress-option--current {
  font-weight: 700;
  background-color: var(--opt-color);
  color: var(--opt-text);
}
.progress-option-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.progress-option-icon {
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
  background: transparent !important;
}
.progress-trigger-icon {
  flex-shrink: 0;
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
  background: transparent !important;
}
.progress-check-icon {
  color: var(--brand-light);
  font-size: 0.75rem;
}
</style>
